/**
 * The mood check-in's vocabulary.
 *
 * Emotion WORDS, not a 1–5 score or faces: putting a feeling into words is
 * what helps (affect labelling), and a number gives the student nothing to
 * recognise themselves in. Each mood answers with one line and one thing to
 * do next, so a check-in never ends in a dead end.
 */
export type MoodId =
  | 'calm'
  | 'motivated'
  | 'okay'
  | 'tired'
  | 'anxious'
  | 'frustrated'
  | 'overwhelmed'
  | 'low';

/** Where a mood's suggestion leads on the Wellbeing page. */
export type MoodActionKind = 'focus' | 'breathe' | 'worry' | 'support';

export interface MoodOption {
  id: MoodId;
  label: string;
  response: string;
  action: { kind: MoodActionKind; label: string };
  /** Shows the support panel alongside the suggestion. */
  low?: boolean;
}

export const MOODS: readonly MoodOption[] = [
  {
    id: 'calm',
    label: 'Calm',
    response: 'Lovely. Calm is a great place to learn from.',
    action: { kind: 'focus', label: 'Start a focus block' },
  },
  {
    id: 'motivated',
    label: 'Motivated',
    response: 'Ride that wave. Pick one thing and go deep.',
    action: { kind: 'focus', label: 'Start a focus block' },
  },
  {
    id: 'okay',
    label: 'Okay',
    response: 'Okay is a fine place to start. A short block often lifts it.',
    action: { kind: 'focus', label: 'Try 25 minutes' },
  },
  {
    id: 'tired',
    label: 'Tired',
    response:
      'A tired brain learns slowly. A minute of breathing, or a proper rest, are both good choices.',
    action: { kind: 'breathe', label: 'Take a breather' },
  },
  {
    id: 'anxious',
    label: 'Anxious',
    response:
      'That is a lot of energy. A minute of slow breathing helps your body settle.',
    action: { kind: 'breathe', label: 'Breathe for a minute' },
  },
  {
    id: 'frustrated',
    label: 'Frustrated',
    response:
      "Frustration usually means you're at the edge of what you know. That's where learning happens. Step away for a minute.",
    action: { kind: 'breathe', label: 'Reset with a breath' },
  },
  {
    id: 'overwhelmed',
    label: 'Overwhelmed',
    response:
      "Let's make it smaller. Write down what's on your mind, then pick one tiny next step.",
    action: { kind: 'worry', label: 'Write it down' },
    low: true,
  },
  {
    id: 'low',
    label: 'Low',
    response:
      "Thanks for telling us. Be gentle with yourself today. You don't have to carry it alone.",
    action: { kind: 'support', label: 'Talk to someone' },
    low: true,
  },
];

export function moodById(id: MoodId | null | undefined): MoodOption | null {
  return MOODS.find(m => m.id === id) ?? null;
}

/** Moods that also reveal the support panel. */
export function isLowMood(id: MoodId | null | undefined): boolean {
  return moodById(id)?.low === true;
}
